if (!customElements.get('quick-cart-drawer')) {
  class QuickCartDrawer extends HTMLElement {
    constructor() {
      super();
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        window.addEventListener('shopify:section:load', this.init.bind(this));
        this.parentElement.addEventListener('shopify:section:select', () => this.open());
        this.parentElement.addEventListener('shopify:section:deselect', () => this.close());
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.toggleState = false;

      if (!this.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }

      this.querySelector('.button--close')?.addEventListener('click', this.close.bind(this));
      this.querySelector('.quick-cart-drawer__backdrop')?.addEventListener('click', this.close.bind(this));

      this.querySelector('.quick-cart-drawer__blocks')?.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.toggleState) {
          this.close();
        }

        if (this.toggleState) {
          const focusableElements = this.querySelectorAll(
            'button, [href], input, select, label, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstFocusableElement = focusableElements[0];
          const lastFocusableElement = focusableElements[focusableElements.length - 1];

          let isTabPressed = e.key === 'Tab';

          if (!isTabPressed) {
            return;
          }

          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              e.preventDefault();
              lastFocusableElement.focus();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              e.preventDefault();
              firstFocusableElement.focus();
            }
          }
        }
      });

      this.initTriggers();
    }

    initTriggers() {
      const observer = new MutationObserver(() => {
        document.querySelectorAll('.quick-cart-drawer__trigger').forEach((element) => {
          if (!element._init) {
            element.addEventListener('click', (event) => {
              this.fetchProductForQuickCartDrawer(event);
            });
            element._init = true;
          }
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    async fetchProductForQuickCartDrawer(event) {
      event.preventDefault();

      /** stop autoplay on quick cart trigger */
      if (event.target.closest('card-product-slider')) {
        event.target.closest('card-product-slider').slider.autoplay.stop();
        event.target.closest('card-product-slider').classList.add('product--open-on-quick-cart');
      }

      const isRecommendations = event.target.classList.contains('quick-cart-drawer__trigger--recommendations');
      const productCard = event.target.closest('product-card');
      let productOptions = null;

      if (!isRecommendations && productCard) {
        // get all checked options
        productOptions = productCard.querySelectorAll('input[type="radio"]:checked, select');
      }

      const trigger = event.target.closest('[data-product-url]');
      if (!trigger?.dataset?.productUrl) {
        console.error('No product URL found');
        return;
      }

      trigger.classList.add('is--loading');

      try {
        const productCardResponse = await fetch(`${trigger.dataset.productUrl}?view=quick-cart`);

        if (!productCardResponse.ok) {
          throw new Error(`HTTP ${productCardResponse.status}`);
        }

        const productCardHTML = await productCardResponse.text();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = productCardHTML;

        const quickCartProduct = tempDiv.querySelector('.quick-cart-product.product');
        if (!quickCartProduct) {
          throw new Error('Quick cart product template not found');
        }

        // Clear and populate main content
        const mainContent = this.querySelector('.quick-cart-drawer__main');
        if (mainContent) {
          mainContent.innerHTML = '';
          mainContent.appendChild(quickCartProduct);
        }

        // Initialize Swiper if exists
        this.initSwiper();

        // Sync options from original product card if not recommendations
        if (!isRecommendations && productCard && productOptions) {
          this.syncProductOptions(productCard, productOptions);
        }

        // Initialize the new product-card component
        this.initQuickCartProductCard();

        // Setup variant change listeners
        this.setupVariantListeners();

        // Short wait before opening drawer
        setTimeout(() => {
          this.open();
        }, 100);
      } catch (error) {
        console.error('Quick cart drawer error:', error);

        // Show error in drawer
        const mainContent = this.querySelector('.quick-cart-drawer__main');
        if (mainContent) {
          mainContent.innerHTML = `
            <div class="quick-cart-drawer__error">
              <p>Unable to load product options. Please try again.</p>
            </div>
          `;
        }

        this.open();
      } finally {
        trigger.classList.remove('is--loading');
      }
    }

    initSwiper() {
      const swiperContainer = this.querySelector('.quick-cart-drawer__media-swiper');
      if (swiperContainer && typeof Swiper !== 'undefined') {
        this.sliderInstance = new Swiper(swiperContainer, {
          slidesPerView: 1.25,
          spaceBetween: 8,
          freeMode: {
            enabled: true,
          },
          breakpoints: {
            750: {
              slidesPerView: 2,
            },
          },
        });
      }
    }

    syncProductOptions(originalProductCard, productOptions) {
      productOptions.forEach((productOption) => {
        const quickCartOption = this.querySelector(`[name="${productOption.name}"][value="${productOption.value}"]`);

        if (quickCartOption) {
          // Clear existing checked states for this option group
          this.querySelectorAll(`[name="${productOption.name}"]`).forEach((input) => {
            input.removeAttribute('checked');
            input.closest('li')?.classList.remove('checked');
          });

          // Set new checked state
          quickCartOption.setAttribute('checked', '');
          quickCartOption.closest('li')?.classList.add('checked');

          // Update label if exists
          const legend = quickCartOption.closest('fieldset')?.querySelector('legend');
          const selectedSpan = legend?.querySelector('[data-selected-variant]');
          if (selectedSpan) {
            selectedSpan.textContent = productOption.value;
          }
        }
      });

      // Sync variant ID
      const originalVariantId = originalProductCard.querySelector('input[name="id"]')?.value;
      const quickCartVariantId = this.querySelector('input[name="id"]');
      if (originalVariantId && quickCartVariantId) {
        quickCartVariantId.value = originalVariantId;
      }
    }

    initQuickCartProductCard() {
      const productCardElement = this.querySelector('product-card');
      if (productCardElement && typeof productCardElement.init === 'function') {
        productCardElement.init();
      }
    }

    setupVariantListeners() {
      this.querySelectorAll('.variant-option-radio-input').forEach((radioInput) => {
        radioInput.addEventListener('change', (event) => {
          // Update legend text
          const legend = event.target.closest('fieldset')?.querySelector('legend');
          const selectedSpan = legend?.querySelector('[data-selected-variant]');
          if (selectedSpan) {
            selectedSpan.textContent = event.target.value;
          }

          // Update media if variant has featured media
          const productCard = this.querySelector('product-card');
          if (productCard && productCard.variantsObj) {
            const selectedVariantId = this.querySelector("input[name='id']")?.value;
            const variant = productCard.variantsObj.find((v) => v.id == selectedVariantId);

            if (variant?.featured_media?.id) {
              this.setActiveMedia(variant.featured_media.id);
            }
          }
        });
      });
    }

    setActiveMedia(id) {
      if (!this.sliderInstance) return;

      const mediaElement = Array.from(this.querySelectorAll('[data-media-id]')).find((media) => Number(media.dataset.mediaId) === id);

      if (mediaElement && mediaElement.dataset.index) {
        this.sliderInstance.slideTo(Number(mediaElement.dataset.index));
      }
    }

    /** Drawer core functions */
    toggle() {
      if (!this.toggleState) {
        this.open();
      } else {
        this.close();
      }
    }

    open() {
      this.toggleState = true;

      // Закриваємо cart-drawer якщо він відкритий
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.classList.contains('active')) {
        cartDrawer.close();
      }

      document.body.classList.add('overflow-hidden');

      const quickCartDrawer = this.querySelector('.quick-cart-drawer__blocks');
      const closeButton = this.querySelector('.button--close');

      if (quickCartDrawer) quickCartDrawer.setAttribute('tabindex', '0');
      if (closeButton) closeButton.setAttribute('tabindex', '0');

      this.classList.add('is--open');
      this.opened();

      // Focus management
      const firstFocusable = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) {
        setTimeout(() => firstFocusable.focus(), 100);
      }
    }

    close() {
      this.toggleState = false;

      const shopTheLookDrawer = document.querySelector('shop-the-look-drawer');

      if (!shopTheLookDrawer || !shopTheLookDrawer.classList.contains('is--open')) {
        document.body.classList.remove('overflow-hidden');
      }

      this.classList.remove('is--open');
      this.closed();
      this.toggleAriaExpanded();

      /** restart autoplay on quick cart drawer close */
      const productSlider = document.querySelector('card-product-slider.product--open-on-quick-cart');
      if (productSlider && productSlider.slider) {
        productSlider.slider.autoplay.start();
        productSlider.classList.remove('product--open-on-quick-cart');
      }
    }

    toggleAriaExpanded(event) {
      if (event) {
        const button = event.target.closest('button');
        if (button) button.setAttribute('aria-expanded', 'true');

        const closeButton = this.querySelector('.button--close');
        if (closeButton) closeButton.setAttribute('aria-expanded', 'true');
      } else {
        document.querySelectorAll('[aria-controls="quick-cart-drawer"]').forEach((button) => {
          button.setAttribute('aria-expanded', 'false');
        });
      }
    }

    opened() {
      const openedEvent = new Event('opened', { bubbles: true });
      this.dispatchEvent(openedEvent);
    }

    closed() {
      const closedEvent = new Event('closed', { bubbles: true });
      this.dispatchEvent(closedEvent);
    }
  }

  customElements.define('quick-cart-drawer', QuickCartDrawer);
}
